/**
 * SendSchedineModal — Form GDPR-compliant per invio schedine alla Polizia
 * via SOAP Alloggiati Web. I dati ospite vengono raccolti in memoria
 * e cancellati immediatamente dopo l'invio (auto-purge).
 *
 * UX flow:
 *  1. Selezione data check-in + numero notti + struttura (se più di una)
 *  2. Compilazione dati ospiti (nome, cognome, sesso, data nascita, cittadinanza, doc)
 *  3. Click "Verifica" → SOAP Test (validazione Ministero senza invio)
 *  4. Click "Invia ora" → SOAP Send → success → form viene azzerato
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, ShieldCheck, UserPlus, Trash, ArrowRight, CheckCircle, Warning, FloppyDisk } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPO_ALLOGGIATO_OPTIONS = [
  { value: 'singolo', label: 'Ospite singolo' },
  { value: 'capofamiglia', label: 'Capofamiglia' },
  { value: 'familiare', label: 'Familiare' },
  { value: 'capogruppo', label: 'Capogruppo' },
  { value: 'membro_gruppo', label: 'Membro gruppo' },
  { value: 'tour_leader', label: 'Tour leader' },
];

const TIPO_DOC_OPTIONS = [
  { value: 'identita', label: "Carta d'identità" },
  { value: 'cart_ident_el', label: "Carta d'identità elettronica" },
  { value: 'passaporto', label: 'Passaporto' },
  { value: 'patente', label: 'Patente' },
  { value: 'perm_soggiorno', label: 'Permesso soggiorno' },
];

const emptyGuest = (commonData = {}) => ({
  tipo_alloggiato: commonData.guests_count > 1 ? 'familiare' : 'singolo',
  check_in: commonData.check_in || '',
  nights: commonData.nights || 1,
  cognome: '',
  nome: '',
  sesso: 'M',
  birth_date: '',
  birth_place_code: '',
  citizenship_code: 'A12000', // Italia ISTAT default
  residence_place_code: '',
  document_type: 'identita',
  document_number: '',
  document_place_code: '',
});

export default function SendSchedineModal({ open, onClose, defaultCheckIn, defaultNights }) {
  const [common, setCommon] = useState({
    check_in: defaultCheckIn || new Date().toISOString().slice(0, 10),
    nights: defaultNights || 1,
  });
  const [guests, setGuests] = useState([emptyGuest({ check_in: defaultCheckIn })]);
  const [strutture, setStrutture] = useState([]);
  const [strutturaId, setStrutturaId] = useState('');
  const [step, setStep] = useState('compile'); // compile | verify | success
  const [verifying, setVerifying] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Try to fetch strutture (silent if endpoint fails)
    axios.get(`${API}/alloggiati/strutture`, { withCredentials: true })
      .then(r => setStrutture(r.data.items || []))
      .catch(() => {});
  }, [open]);

  // When common.check_in or common.nights change, propagate to all guests
  useEffect(() => {
    setGuests(prev => prev.map(g => ({ ...g, check_in: common.check_in, nights: common.nights })));
  }, [common.check_in, common.nights]);

  if (!open) return null;

  const updateGuest = (i, field, value) => {
    setGuests(prev => prev.map((g, idx) => idx === i ? { ...g, [field]: value } : g));
  };

  const addGuest = () => {
    if (guests.length >= 20) {
      toast.error('Massimo 20 ospiti per invio');
      return;
    }
    setGuests(prev => [...prev, emptyGuest({ check_in: common.check_in, nights: common.nights, guests_count: prev.length + 1 })]);
  };

  const removeGuest = (i) => {
    if (guests.length === 1) {
      toast.error('Almeno un ospite richiesto');
      return;
    }
    setGuests(prev => prev.filter((_, idx) => idx !== i));
  };

  const isComplete = (g) =>
    g.cognome.trim() && g.nome.trim() && g.birth_date && g.birth_place_code &&
    g.citizenship_code && g.residence_place_code && g.document_number.trim() && g.document_place_code;

  const allComplete = guests.every(isComplete) && common.check_in && common.nights >= 1 && common.nights <= 30;

  const verifyNow = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const r = await axios.post(`${API}/alloggiati/test-schedine`, { guests }, { withCredentials: true });
      setVerifyResult(r.data);
      if (r.data.ok) {
        toast.success('Tracciati validi · pronti per l\'invio');
        setStep('verify');
      } else {
        const msg = r.data.errors ? r.data.errors.map(e => e.guest + ': ' + e.error).join(' · ') : (r.data.error_desc || 'Errori di validazione');
        toast.error('Errori: ' + msg, { duration: 8000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore connessione Alloggiati');
    } finally {
      setVerifying(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm(`Confermi l'invio di ${guests.length} schedin${guests.length === 1 ? 'a' : 'e'} alla Polizia di Stato? I dati ospite verranno cancellati subito dopo l'invio.`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/alloggiati/send-schedine`, { guests }, { withCredentials: true });
      if (r.data.ok) {
        toast.success(`✅ ${guests.length} schedin${guests.length === 1 ? 'a inviata' : 'e inviate'}. Ricevuta disponibile da domani.`, { duration: 6000 });
        // GDPR purge: clear all guest data from local state
        setGuests([emptyGuest({ check_in: common.check_in })]);
        setStep('success');
      } else {
        toast.error('Polizia ha rifiutato: ' + (r.data.error_desc || r.data.error_detail || 'Errore'), { duration: 10000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-6" onClick={() => !sending && !verifying && onClose()} data-testid="send-schedine-modal">
      <div className="bg-white max-w-3xl w-full my-4" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-zinc-200 sticky top-0 bg-white">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-800 text-white flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={18} weight="bold" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-wider text-red-700 mb-0.5">POLIZIA DI STATO · ALLOGGIATI WEB</p>
              <p className="text-sm font-black text-zinc-900 tracking-tight truncate">Invia schedine ospiti</p>
            </div>
          </div>
          <button onClick={onClose} disabled={sending || verifying} className="text-zinc-400 hover:text-zinc-700 p-1" data-testid="send-schedine-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* GDPR notice */}
        <div className="bg-emerald-50 border-l-4 border-emerald-500 px-4 py-3 sm:px-5 mx-4 sm:mx-5 mt-4 flex items-start gap-2.5">
          <ShieldCheck size={14} weight="bold" className="text-emerald-700 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-wider text-emerald-900 mb-0.5">GDPR · Auto-Purge</p>
            <p className="text-[11px] text-emerald-900 leading-snug">
              I dati ospite restano in memoria solo per il tempo dell'invio. Subito dopo la conferma di trasmissione vengono <strong>cancellati immediatamente</strong> dal nostro server. La conservazione 5 anni è competenza esclusiva del Ministero. Base giuridica: <strong>Art. 109 TULPS · Art. 6.1.c GDPR</strong>.
            </p>
          </div>
        </div>

        {step === 'success' ? (
          <div className="p-8 text-center" data-testid="send-success">
            <div className="w-16 h-16 mx-auto bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3 rounded-full">
              <CheckCircle size={32} weight="fill" />
            </div>
            <p className="text-lg font-black text-zinc-900 mb-1 tracking-tight">Invio completato</p>
            <p className="text-sm text-zinc-500 mb-1">I dati ospite sono stati cancellati dal sistema (GDPR).</p>
            <p className="text-xs text-zinc-400 mb-5">La ricevuta PDF sarà scaricabile automaticamente domani mattina dal portale Polizia.</p>
            <Button onClick={onClose} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-bold uppercase tracking-wider h-10 px-5">
              Chiudi
            </Button>
          </div>
        ) : (
          <>
            {/* Common data */}
            <div className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-3 gap-3 border-b border-zinc-100">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">Data check-in</label>
                <input type="date" value={common.check_in} onChange={(e) => setCommon({ ...common, check_in: e.target.value })} className="w-full h-9 px-3 border border-zinc-300 text-sm" data-testid="schedine-check-in" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">Notti permanenza</label>
                <input type="number" min="1" max="30" value={common.nights} onChange={(e) => setCommon({ ...common, nights: Math.min(30, Math.max(1, Number(e.target.value) || 1)) })} className="w-full h-9 px-3 border border-zinc-300 text-sm tabular-nums" data-testid="schedine-nights" />
              </div>
              {strutture.length > 1 && (
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">Struttura</label>
                  <select value={strutturaId} onChange={(e) => setStrutturaId(e.target.value)} className="w-full h-9 px-3 border border-zinc-300 text-sm" data-testid="schedine-struttura">
                    <option value="">— Tutte —</option>
                    {strutture.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              )}
            </div>

            {/* Guests list */}
            <div className="p-4 sm:p-5 space-y-3">
              {guests.map((g, i) => (
                <div key={i} className="border border-zinc-200 p-3 sm:p-4" data-testid={`guest-form-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] font-black uppercase tracking-wider text-zinc-700">
                      Ospite {i + 1} {isComplete(g) && <CheckCircle size={11} weight="fill" className="inline ml-1 text-emerald-600" />}
                    </p>
                    {guests.length > 1 && (
                      <button onClick={() => removeGuest(i)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`guest-remove-${i}`}><Trash size={12} weight="bold" /></button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    <FormField label="Tipo">
                      <select value={g.tipo_alloggiato} onChange={(e) => updateGuest(i, 'tipo_alloggiato', e.target.value)} className="w-full h-9 px-2 border border-zinc-300 text-sm">
                        {TIPO_ALLOGGIATO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </FormField>
                    <FormField label="Cognome *">
                      <input type="text" value={g.cognome} onChange={(e) => updateGuest(i, 'cognome', e.target.value.toUpperCase())} placeholder="ROSSI" className="w-full h-9 px-2 border border-zinc-300 text-sm uppercase" data-testid={`guest-${i}-cognome`} />
                    </FormField>
                    <FormField label="Nome *">
                      <input type="text" value={g.nome} onChange={(e) => updateGuest(i, 'nome', e.target.value.toUpperCase())} placeholder="MARIO" className="w-full h-9 px-2 border border-zinc-300 text-sm uppercase" data-testid={`guest-${i}-nome`} />
                    </FormField>
                    <FormField label="Sesso *">
                      <select value={g.sesso} onChange={(e) => updateGuest(i, 'sesso', e.target.value)} className="w-full h-9 px-2 border border-zinc-300 text-sm">
                        <option value="M">M</option><option value="F">F</option>
                      </select>
                    </FormField>
                    <FormField label="Data nascita *">
                      <input type="date" value={g.birth_date} onChange={(e) => updateGuest(i, 'birth_date', e.target.value)} className="w-full h-9 px-2 border border-zinc-300 text-sm" data-testid={`guest-${i}-birth-date`} />
                    </FormField>
                    <FormField label="Cod. Comune nascita *">
                      <input type="text" value={g.birth_place_code} onChange={(e) => updateGuest(i, 'birth_place_code', e.target.value.toUpperCase())} placeholder="ISTAT 9 char" maxLength="9" className="w-full h-9 px-2 border border-zinc-300 text-sm uppercase tabular-nums" data-testid={`guest-${i}-birth-place`} />
                    </FormField>
                    <FormField label="Cittadinanza *">
                      <input type="text" value={g.citizenship_code} onChange={(e) => updateGuest(i, 'citizenship_code', e.target.value.toUpperCase())} placeholder="ISTAT 9 char (es. A12000 = Italia)" maxLength="9" className="w-full h-9 px-2 border border-zinc-300 text-sm uppercase tabular-nums" data-testid={`guest-${i}-citizenship`} />
                    </FormField>
                    <FormField label="Cod. Comune residenza *">
                      <input type="text" value={g.residence_place_code} onChange={(e) => updateGuest(i, 'residence_place_code', e.target.value.toUpperCase())} placeholder="ISTAT 9 char" maxLength="9" className="w-full h-9 px-2 border border-zinc-300 text-sm uppercase tabular-nums" data-testid={`guest-${i}-residence`} />
                    </FormField>
                    <FormField label="Tipo documento *">
                      <select value={g.document_type} onChange={(e) => updateGuest(i, 'document_type', e.target.value)} className="w-full h-9 px-2 border border-zinc-300 text-sm">
                        {TIPO_DOC_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </FormField>
                    <FormField label="Numero doc. *">
                      <input type="text" value={g.document_number} onChange={(e) => updateGuest(i, 'document_number', e.target.value.toUpperCase())} placeholder="AB1234567" maxLength="20" className="w-full h-9 px-2 border border-zinc-300 text-sm uppercase tabular-nums" data-testid={`guest-${i}-doc-number`} />
                    </FormField>
                    <FormField label="Cod. luogo rilascio doc. *">
                      <input type="text" value={g.document_place_code} onChange={(e) => updateGuest(i, 'document_place_code', e.target.value.toUpperCase())} placeholder="ISTAT 9 char" maxLength="9" className="w-full h-9 px-2 border border-zinc-300 text-sm uppercase tabular-nums" data-testid={`guest-${i}-doc-place`} />
                    </FormField>
                  </div>
                </div>
              ))}

              <Button onClick={addGuest} variant="outline" className="rounded-none border-dashed border-zinc-300 hover:border-zinc-900 text-xs font-bold uppercase tracking-wider w-full h-10" data-testid="schedine-add-guest">
                <UserPlus size={13} weight="bold" className="mr-2" /> Aggiungi ospite ({guests.length})
              </Button>

              {/* ISTAT codes helper */}
              <div className="bg-blue-50 border border-blue-200 p-3">
                <p className="text-[11px] text-blue-900 leading-snug">
                  💡 <strong>Codici ISTAT:</strong> per i comuni italiani usa il codice catastale (es. <code>H501</code> per Roma, <code>F205</code> per Milano). Per cittadinanze estere il prefisso è <code>Z+codice</code> (es. <code>Z110</code> = Francia, <code>Z210</code> = Germania). Per Italia usa <code>A12000</code>. <a href="https://www.istat.it/codici-territoriali" target="_blank" rel="noreferrer" className="underline">Lookup completo →</a>
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 sm:p-5 border-t border-zinc-200 bg-zinc-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sticky bottom-0">
              <div className="text-[11px] text-zinc-600 leading-tight">
                <strong>{guests.length}</strong> ospit{guests.length === 1 ? 'e' : 'i'} · {guests.filter(isComplete).length}/{guests.length} compilati
              </div>
              <div className="flex gap-2">
                <Button onClick={verifyNow} disabled={!allComplete || verifying} variant="outline" className="rounded-none border-zinc-300 text-xs font-bold uppercase tracking-wider h-10" data-testid="schedine-verify-btn">
                  {verifying ? 'Verifico...' : (
                    <><CheckCircle size={12} weight="bold" className="mr-1.5" /> Verifica</>
                  )}
                </Button>
                <Button onClick={sendNow} disabled={!allComplete || sending || step !== 'verify'} className="rounded-none bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider h-10" data-testid="schedine-send-btn">
                  {sending ? 'Invio in corso...' : (
                    <><ShieldCheck size={12} weight="bold" className="mr-1.5" /> Invia ora <ArrowRight size={12} weight="bold" className="ml-1" /></>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label className="text-[9px] font-bold uppercase tracking-wider text-zinc-500 block mb-0.5">{label}</label>
      {children}
    </div>
  );
}
